import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { RiderAccessModal } from '../components/RiderAccessModal';

export const LoginPage: React.FC = () => {
  const { user, login, backendUser, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasCheckedRole, setHasCheckedRole] = useState(false);
  const [isLoggingOutRider, setIsLoggingOutRider] = useState(false);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for payment success in URL or sessionStorage
  const paymentSuccess = searchParams.get('payment') === 'success' || sessionStorage.getItem('payment_success') === 'true';
  const paymentSessionId = sessionStorage.getItem('payment_session_id');

  // Show payment success message if coming from payment flow
  useEffect(() => {
    if (paymentSuccess && !user) {
      toast.success('Payment successful! Please log in to continue.');
    }
  }, [paymentSuccess, user]);

  // Reset hasCheckedRole when user logs out
  useEffect(() => {
    if (!user) {
      setHasCheckedRole(false);
      setIsLoggingOutRider(false);
      setShowRiderModal(false);
    }
  }, [user]);
  
  // Handle modal confirmation - navigate to rider login (modal handles navigation)
  const handleRiderModalConfirm = async () => {
    // Modal will navigate to rider login page
    // Logout here to clear admin panel session
    setShowRiderModal(false);
    setIsLoggingOutRider(true);
    await logout().catch(console.error);
  };

  // Handle all redirects after login - use useEffect to avoid multiple navigation events
  useEffect(() => {
    if (!user || !backendUser) return;
    
    // Prevent multiple checks
    if (hasCheckedRole) return;
    
    // Don't navigate if we're in the process of logging out a regular rider
    if (isLoggingOutRider) return;
    
    // Wait for backendUser.role to be loaded before checking
    // If role is undefined, wait (backendUser might still be loading from API)
    if (!backendUser.role) {
      console.log('⏳ LoginPage: Waiting for backendUser.role to load...', { backendUser });
      return;
    }
    
    // Check if user is a regular rider (USER role) - they should use the mobile app
    // Only allow explicit admin roles - block everything else
    const allowedRoles = ['SYSTEM_ADMIN', 'SUPERADMIN', 'STORE_ADMIN', 'STORE_MANAGER', 'ADMIN'];
    const userRole = backendUser.role;
    
    console.log('🔍 LoginPage role check:', { userRole, backendUserRole: backendUser.role, allowedRoles });
    
    if (!allowedRoles.includes(userRole)) {
      // Regular riders or users without explicit admin role should use the mobile app
      console.log('🚫 Blocking regular rider from admin panel');
      setHasCheckedRole(true);
      setShowRiderModal(true); // Show modal instead of toast
      return; // CRITICAL: Don't navigate anywhere
    }

    // User has valid role - mark as checked and proceed
    console.log('✅ Allowing admin user access');
    setHasCheckedRole(true);
    toast.success('Login successful');

    // If coming from payment flow, redirect to appropriate page
    if (paymentSuccess) {
      // Clear payment success flags
      sessionStorage.removeItem('payment_success');
      sessionStorage.removeItem('payment_session_id');
      
      const redirectPath = (backendUser.role === 'SUPERADMIN' || backendUser.role === 'SYSTEM_ADMIN')
        ? '/stores'
        : backendUser.storeId
        ? `/stores/${backendUser.storeId}`
        : '/dashboard';
      
      // Use replace to prevent navigation history issues
      navigate(redirectPath, { replace: true });
      return;
    }

    // Default redirect to dashboard if user is logged in
    navigate('/dashboard', { replace: true });
  }, [user, backendUser, paymentSuccess, navigate, logout, hasCheckedRole, isLoggingOutRider]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // CRITICAL: Clear any existing sessions before logging in
      // This ensures we start with a clean slate
      try {
        const { signOut } = await import('firebase/auth');
        const { auth } = await import('../services/firebase');
        if (auth.currentUser) {
          console.log('🧹 Clearing existing session before login...');
          await signOut(auth);
          localStorage.clear();
          sessionStorage.clear();
          // Small delay to ensure cleanup completes
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (cleanupError) {
        console.warn('Could not clear existing session:', cleanupError);
        // Continue anyway - login will handle it
      }

      await login(email, password);
      // Don't show success toast yet - wait for role check in useEffect
      // Navigation will be handled by useEffect when user and backendUser are set
    } catch (error: any) {
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Always show form if we're logging out a rider
  if (isLoggingOutRider) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <p className="text-gray-600">Signing you out...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Return null ONLY if we've confirmed the user has a valid admin role and we're redirecting
  // Don't return null until hasCheckedRole is true (meaning the role check completed)
  if (user && backendUser && hasCheckedRole && !isLoggingOutRider) {
    const allowedRoles = ['SYSTEM_ADMIN', 'SUPERADMIN', 'STORE_ADMIN', 'STORE_MANAGER', 'ADMIN'];
    const userRole = backendUser.role || 'USER';
    const isRegularRider = !allowedRoles.includes(userRole);
    // Only hide form if user is confirmed to have valid admin role
    // If it's a regular rider, keep showing the form (logout should have happened)
    if (!isRegularRider) {
      return null; // Valid admin - hide form while redirecting
    }
  }

  return (
    <>
      <RiderAccessModal 
        isOpen={showRiderModal} 
        onConfirm={handleRiderModalConfirm} 
      />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              RideConnect Admin
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Sign in to your admin account
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field mt-1"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input-field mt-1"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
